import React, { useEffect, useRef, useState } from 'react';
import { plannedSchedule } from './plannedSegments';
import './RRHHNative.css';

function request(path, options = {}) {
  return fetch(`/api/rrhh${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
  }).then(async (response) => {
    const payload = await response.json().catch(() => null);
    if (!response.ok) throw new Error(payload?.error || 'No se pudo conectar con el reloj facial');
    return payload;
  });
}

function loadScript(source) {
  return new Promise((resolve, reject) => {
    const current = document.querySelector(`script[data-rrhh-face-script="${source}"]`);
    if (current) return current.dataset.loaded === 'true' ? resolve() : current.addEventListener('load', resolve, { once: true });
    const script = document.createElement('script');
    script.src = source; script.async = true; script.dataset.rrhhFaceScript = source;
    script.addEventListener('load', () => { script.dataset.loaded = 'true'; resolve(); }, { once: true });
    script.addEventListener('error', () => reject(new Error('No se pudo cargar el reconocimiento facial')), { once: true });
    document.head.appendChild(script);
  });
}

function captureFrame(video, canvas) {
  const width = video?.videoWidth || 0;
  const height = video?.videoHeight || 0;
  if (!width || !height || !canvas) throw new Error('La cámara todavía no está lista');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  context.drawImage(video, 0, 0, width, height);
  return canvas.toDataURL('image/jpeg', 0.92);
}

function imageFromDataUrl(source) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('No se pudo preparar la captura facial'));
    image.src = source;
  });
}

function formatElapsed(totalSeconds) {
  const safeSeconds = Math.max(0, Number(totalSeconds) || 0);
  const hours = String(Math.floor(safeSeconds / 3600)).padStart(2, '0');
  const minutes = String(Math.floor((safeSeconds % 3600) / 60)).padStart(2, '0');
  const seconds = String(safeSeconds % 60).padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
}

export default function RelojFacialPage() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const [device, setDevice] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [recognizing, setRecognizing] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState('');
  const [person, setPerson] = useState(null);
  const [validationToken, setValidationToken] = useState('');
  const [openTramo, setOpenTramo] = useState(false);
  const [activeTramo, setActiveTramo] = useState(null);
  const [planReference, setPlanReference] = useState(null);
  const [references, setReferences] = useState([]);
  const [selectedReference, setSelectedReference] = useState('');
  const [completedMark, setCompletedMark] = useState(null);
  const [completionElapsed, setCompletionElapsed] = useState(0);
  const [activeElapsed, setActiveElapsed] = useState(0);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let mounted = true;
    async function boot() {
      try {
        const session = await request(`/facial/sesion?token=${encodeURIComponent(token)}`);
        await Promise.all([
          loadScript('/rrhh-assets/vendor/face-api/face-api.min.js'),
          loadScript('/rrhh-assets/face-descriptor.js'),
        ]);
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 960 }, height: { ideal: 720 } }, audio: false });
        if (!mounted) { stream.getTracks().forEach((track) => track.stop()); return; }
        streamRef.current = stream;
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setDevice(session.device); setCameraReady(true);
      } catch (err) { if (mounted) setError(err.message || 'No se pudo iniciar la cámara'); }
    }
    boot();
    return () => { mounted = false; streamRef.current?.getTracks().forEach((track) => track.stop()); };
  }, [token]);

  useEffect(() => {
    if (!completedMark) return undefined;
    const startedAt = completedMark.occurredAt;
    const updateElapsed = () => setCompletionElapsed(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    updateElapsed();
    const intervalId = window.setInterval(updateElapsed, 250);
    const timeoutId = window.setTimeout(() => {
      setPerson(null);
      setValidationToken('');
      setOpenTramo(false);
      setActiveTramo(null);
      setPlanReference(null);
      setReferences([]);
      setSelectedReference('');
      setCapturedPhoto('');
      setCompletedMark(null);
      setCompletionElapsed(0);
      videoRef.current?.play().catch(() => {});
    }, 3000);
    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [completedMark]);

  useEffect(() => {
    if (!openTramo || !activeTramo?.entrada_at || completedMark) {
      setActiveElapsed(0);
      return undefined;
    }
    const start = new Date(activeTramo.entrada_at).getTime();
    const updateElapsed = () => setActiveElapsed(Math.max(0, Math.floor((Date.now() - start) / 1000)));
    updateElapsed();
    const intervalId = window.setInterval(updateElapsed, 1000);
    return () => window.clearInterval(intervalId);
  }, [activeTramo?.entrada_at, completedMark, openTramo]);

  async function recognize() {
    if (!cameraReady || recognizing) return;
    setRecognizing(true); setError(''); setNotice('');
    try {
      // La validación siempre usa una foto fija: evita comparar un rostro que
      // cambió entre la interacción de la persona y la detección del modelo.
      const photo = captureFrame(videoRef.current, canvasRef.current);
      videoRef.current?.pause();
      setCapturedPhoto(photo);
      const descriptor = await window.buildFaceDescriptorFromImage(await imageFromDataUrl(photo));
      const result = await request('/facial/validar', {
        method: 'POST', body: JSON.stringify({ token, descriptor, descriptor_version: window.LANG_FACE_DESCRIPTOR_VERSION }),
      });
      if (!result.ok) throw new Error(result.message || 'No se reconoció el rostro');
      setPerson(result.persona);
      setValidationToken(result.validation_token);
      setOpenTramo(Boolean(result.tramo_abierto));
      setActiveTramo(result.tramo_activo || null);
      setPlanReference(result.referencia_plan || null);
      setReferences(Array.isArray(result.referencias) ? result.referencias : []);
      setSelectedReference(result.referencia_seleccionada || result.referencias?.[0] || '');
      setNotice(`${result.persona.nombre}, confirmá ${result.tramo_abierto ? 'salida' : 'entrada'}.`);
    } catch (err) {
      setCapturedPhoto('');
      videoRef.current?.play().catch(() => {});
      setError(err.message || 'No se pudo reconocer el rostro');
    }
    finally { setRecognizing(false); }
  }

  function resetRecognition() {
    setPerson(null);
    setValidationToken('');
    setOpenTramo(false);
    setActiveTramo(null);
    setPlanReference(null);
    setReferences([]);
    setSelectedReference('');
    setCompletedMark(null);
    setCompletionElapsed(0);
    setCapturedPhoto('');
    videoRef.current?.play().catch(() => {});
  }

  async function mark(type) {
    if (!validationToken || !person) return;
    setRecognizing(true); setError('');
    try {
      await request('/facial/marcas', {
        method: 'POST',
        body: JSON.stringify({ token, validation_token: validationToken, tipo: type, referencia: selectedReference }),
      });
      setNotice('');
      setCompletedMark({
        type,
        personName: person.nombre,
        reference: selectedReference,
        occurredAt: Date.now(),
      });
    } catch (err) { setError(err.message || 'No se pudo registrar la marca'); }
    finally { setRecognizing(false); }
  }

  const markingType = openTramo ? 'SALIDA' : 'ENTRADA';

  return <main className="rrhh-face-clock-page">
    <section className={`rrhh-face-clock-card${person ? ' is-recognized' : ''}`}>
      <header>
        <span className="rrhh-face-clock-kicker">RELOJ FACIAL</span>
        <h1>{device?.nombre || 'Cargando dispositivo...'}</h1>
        <p>{device?.lugar ? `${device.lugar} · ` : ''}Identificá tu rostro y registrá tu jornada.</p>
      </header>
      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-success">{notice}</div>}
      <div className={`rrhh-face-clock-workspace${person ? ' is-recognized' : ''}`}>
        <aside className="rrhh-face-capture-panel">
          {person && <div className="rrhh-face-greeting">Hola, <strong>{person.nombre}</strong></div>}
          <div className={`rrhh-face-camera${capturedPhoto ? ' is-captured' : ''}`}>
            <video ref={videoRef} muted playsInline />
            {capturedPhoto && <img src={capturedPhoto} alt={`Captura facial de ${person?.nombre || 'la persona'}`} />}
            <canvas ref={canvasRef} hidden />
          </div>
        </aside>
        {!person ? (
          <button className="btn btn-primary rrhh-face-main-action" type="button" disabled={!cameraReady || recognizing} onClick={recognize}>
            {recognizing ? 'Reconociendo...' : 'Reconocer rostro'}
          </button>
        ) : completedMark ? (
          <section className={`rrhh-face-mark-complete ${completedMark.type.toLowerCase()}`}>
            <span>{completedMark.type === 'ENTRADA' ? 'TURNO INICIADO' : 'TURNO FINALIZADO'}</span>
            <strong>{completedMark.personName}</strong>
            <p>{completedMark.reference}</p>
            {completedMark.type === 'ENTRADA' && <output>{new Date(completionElapsed * 1000).toISOString().slice(11, 19)}</output>}
            <small>Volviendo al reloj...</small>
          </section>
        ) : (
          <section className="rrhh-face-confirmation">
            <div className="rrhh-face-person-heading">
              <span>{openTramo ? 'Hay una entrada abierta. Confirmá la salida.' : 'No hay una jornada abierta. Confirmá la entrada.'}</span>
            </div>
            <div className="rrhh-face-plan-reference">
              <span>REFERENCIA DEL PLAN</span>
              <strong>{planReference?.detalle || 'Sin turno previsto'}</strong>
              {planReference?.hora_inicio && <small>{plannedSchedule(planReference)}</small>}
            </div>
            {openTramo && <div className="rrhh-face-active-tramo">
              <div>
                <span>JORNADA ABIERTA</span>
                <strong>{activeTramo?.detalle || 'Sin referencia'}</strong>
                {activeTramo?.entrada_at && <small>Ingreso: {new Date(activeTramo.entrada_at).toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit', hour12: false, hourCycle: 'h23' })}</small>}
              </div>
              <output className="rrhh-face-active-timer">{formatElapsed(activeElapsed)}</output>
            </div>}
            <label className="rrhh-face-reference-select">
              Referencia para la marca
              <select value={selectedReference} onChange={(event) => setSelectedReference(event.target.value)}>
                {references.map((reference) => <option key={reference} value={reference}>{reference}</option>)}
              </select>
            </label>
            <div className="rrhh-face-mark-actions">
              <button className="btn btn-secondary" type="button" onClick={resetRecognition}>Cancelar</button>
              <button className={`rrhh-face-mark-button ${markingType.toLowerCase()}`} type="button" disabled={recognizing || !selectedReference} onClick={() => mark(markingType)}>
                Marcar {markingType.toLowerCase()}
              </button>
            </div>
          </section>
        )}
      </div>
    </section>
  </main>;
}
